import React, { useMemo } from "react";
import { Alert, Button, Skeleton } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import marketplaceService from "../../service/marketplaceService";
import organizationservice from "../../service/organizationservice";
import pathnames, {
  marketplaceItemPath,
} from "../../constants/pathnames";
import { normalizePlans } from "./utils";
import ItemForm from "./components/ItemForm";
import "./marketplace.scss";

/**
 * B3 — Edit an existing marketplace catalogue item (full-page form).
 */
const EditMarketplaceItem = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const itemQuery = useQuery({
    retry: false,
    enabled: Boolean(id),
    queryKey: ["marketplace-item", id],
    queryFn: () => marketplaceService.getItem(id),
  });

  const metaQuery = useQuery({
    retry: false,
    queryKey: ["marketplace-meta"],
    queryFn: () => marketplaceService.getMeta(),
  });

  const plansQuery = useQuery({
    retry: false,
    queryKey: ["subscription-plans"],
    queryFn: () => organizationservice.getSubscriptionPlans(),
  });

  const plans = useMemo(() => normalizePlans(plansQuery.data), [plansQuery.data]);
  const item = itemQuery.data;
  const backToCatalogue = () => navigate(pathnames.MARKETPLACE_ADDONS);

  if (itemQuery.isPending) {
    return (
      <AppLayout>
        <Header title="Edit marketplace item" description="Loading…" />
        <div className="p-3 bg-light marketplace-page">
          <Skeleton variant="rounded" height={48} className="mb-3" />
          <Skeleton variant="rounded" height={420} />
        </div>
      </AppLayout>
    );
  }

  if (!item) {
    return (
      <AppLayout>
        <Header title="Edit marketplace item" description="Item not found" />
        <div className="p-3">
          <Alert severity="warning" className="mb-3">
            Could not load this catalogue item.
          </Alert>
          <Button startIcon={<ArrowBack />} onClick={backToCatalogue}>
            Back to catalogue
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Header
        title="Edit marketplace item"
        description={item.name || item.code || "Update catalogue pricing, eligibility, and capacity"}
      />
      <div className="p-3 bg-light marketplace-page" style={{ minHeight: "100%" }}>
        <div className="mp-detail-toolbar">
          <Button startIcon={<ArrowBack />} color="inherit" onClick={backToCatalogue}>
            Back to catalogue
          </Button>
        </div>

        <div className="mp-item-shell mp-item-card">
          <h1>Edit marketplace item</h1>
          <p className="sub">
            Update catalogue pricing, eligibility, and capacity settings.
          </p>
          <ItemForm
            mode="edit"
            item={item}
            plans={plans}
            meta={metaQuery.data}
            onCancel={backToCatalogue}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["marketplace-item", id] });
              queryClient.invalidateQueries({ queryKey: ["marketplace-items"] });
              queryClient.invalidateQueries({ queryKey: ["marketplace-items-stats"] });
              navigate(marketplaceItemPath(id));
            }}
          />
        </div>
      </div>
    </AppLayout>
  );
};

export default EditMarketplaceItem;
